// React and MobX
import React, { useState, useEffect } from "react";
import { useObserver } from "mobx-react-lite";

// Material-UI Core and Styles
import { Box, Button, Grid, Typography, Paper } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import LockIcon from "@material-ui/icons/Lock";

// External Libraries
import Stickyfill from "stickyfilljs";

// Core
import { Loader } from "Core/Components/Loader";
import { useRouter } from "Core/Utils";

// Custom Components
import { Error } from "Custom/Components/Error";
import { CardPayment } from "Custom/Components/PaymentMethods/CardPayment";

// Custom Styles
import { DashboardPageStyles, usePageAnimationStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";
import { PaymentHistoryStyles, PaymentMethods, PaymentSectionStyles, RenderPaymentHeaderStyle } from "Custom/StylesAppSpecific/DashboardSubViews/PropertyPageStyling";

// Custom Content
import logoMastercard from "Custom/Content/logo-mastercard.svg";
import logoVisa from "Custom/Content/logo-visa.svg";

// Custom ViewModels
import { PropertyViewModel } from "Custom/ViewModels/DashboardSubViewModels/PropertyViewModel";
import { BuyViewModel } from "Custom/ViewModels/BuyViewModel";

// Enums and Models
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";
import { StoresInstance } from "Core/Base";
import { Elements } from "@stripe/react-stripe-js";

declare var Stripe: any;

export const PaymentSection: React.FC = () => {
    const router = useRouter();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;

    const propertyId = (router.match.params as any).propertyId;

    const pageAnimationStyles = usePageAnimationStyles();
    const paymentSectionStyles = PaymentSectionStyles();
    const dashboardPageStyles = DashboardPageStyles();
    const paymentHistoryStyles = PaymentHistoryStyles();
    const renderPaymentHeaderStyle = RenderPaymentHeaderStyle();

    // Use the shared viewModel
    const [viewModel] = useState(() => new PropertyViewModel(propertyId));

    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [buyViewModel] = React.useState(() => BuyViewModel.Instance);
    const paymentMethods = React.useRef<HTMLDivElement | null>(null);
    const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
    const [isStripeInitializing, setIsStripeInitializing] = useState(true);

    const renderBusy = () => <Loader loadingText="Fetching payments..." />;
    const renderError = () => <Error errorText="Failed to retrieve payments information." />;

    useEffect(() => {
        if (paymentMethods.current) {
            Stickyfill.addOne(paymentMethods.current);
        }

        // Initialize Stripe
        const initializeStripe = async () => {
            setIsStripeInitializing(true);
            const stripeKey = StoresInstance.domain.StripeStore.publishableKey || StoresInstance.domain.AccountStore.StripePublicKey;
            if (stripeKey) {
                // const stripeKey = StoresInstance.domain.StripeStore.publishableKey;
                setStripePromise(Stripe(stripeKey));
            } else {
                console.warn("No Stripe publishable key found.");
            }

            setIsStripeInitializing(false);
        };

        initializeStripe();

        // Cleanup function
        return () => {
            if (paymentMethods.current) {
                Stickyfill.removeOne(paymentMethods.current);
            }
        };
    }, []);

    const setFinish = () => {
        setPaymentSuccess(true);
        viewModel.setPaymentComplete();
    };

    const canDisplayPaymentRow = (): boolean => {
        return (
            viewModel.showStripePaymentRequest &&
            viewModel.hasStripePaymentRequest &&
            (!viewModel.hasPaidStripeReservationPaymentRequest || !viewModel.hasPaidStripeOrderPaymentRequest)
        );
    };

    const canDisplayPaymentHistoryRow = (): boolean => {
        return viewModel.hasPaidStripeReservationPaymentRequest || viewModel.hasPaidStripeOrderPaymentRequest;
    };

    const renderSuccessMethod = () =>
        !paymentSuccess ? (
            <div className="paymentSuccess-box">
                <div className="paymentSuccess-box__inner">
                    <span className="paymentSuccess-box__icon">
                        <CheckCircleOutlineIcon />
                    </span>
                    <h6 className="paymentSuccess-box__title">Success</h6>
                </div>
                <Button className="paymentSuccess-box__btn" onClick={() => setFinish()}>
                    Finish
                </Button>
            </div>
        ) : (
            ""
        );

    const renderPaymentHeader = (): JSX.Element => (
        <Box className={renderPaymentHeaderStyle.payMentHeader}>
            <Typography className="title" component="h2">
                Payments
            </Typography>
        </Box>
    );

    const renderPaymentDetailsRow = (): JSX.Element => (
        <Box className={paymentSectionStyles.paymentRow} style={{ display: "grid" }}>
            <Grid container spacing={0} className="payment-module">
                <Grid item md={12} xs={12}>
                    <Typography className={paymentSectionStyles.paymentRowTitle} variant="h5">
                        Payments outstanding
                    </Typography>
                    <Box>
                        <Box className={paymentSectionStyles.paymentRowSubTitle}>
                            <div>Reservation fee:</div>
                            <div>{viewModel.getReservationStripePaymentRequest}</div>
                        </Box>

                        <Box className={paymentSectionStyles.paymentRowSubTitle}>
                            <div>Choices:</div>
                            <div>{viewModel.getChoicesStripePaymentRequest}</div>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            {canDisplayPaymentHistoryRow() && <Box className={paymentSectionStyles.titleBorder} />}
        </Box>
    );

    const renderPaymentMethod = (): JSX.Element => {
        if (isStripeInitializing) {
            return <Loader loadingText="Initializing payment system..." />;
        }

        if (!stripePromise) {
            return (
                <Typography variant="body2" style={{ color: "#525f7f" }}>
                    Payments are not available for this company at the moment.
                </Typography>
            );
        }

        if (viewModel.getPaymentPrice === 0) {
            return (
                <Typography variant="body2" style={{ color: "#525f7f" }}>
                    No outstanding payments to process at this time.
                </Typography>
            );
        }

        return (
            <Elements stripe={stripePromise}>
                <Box>
                    <Paper elevation={0} style={{ padding: "20px", marginBottom: "20px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <CreditCardIcon style={{ fontSize: 40, color: "#6772e5" }} />
                            </Grid>
                            <Grid item xs>
                                <Typography variant="h6" style={{ fontWeight: 600, color: "#32325d" }}>
                                    Secure Payment
                                </Typography>
                                <Typography variant="body2" style={{ color: "#525f7f" }}>
                                    Your payment is processed securely by Stripe. We accept all major credit cards.
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Box display="flex" alignItems="center">
                                    <img src={logoVisa} alt="Visa" style={{ height: "25px", marginRight: "10px" }} />
                                    <img src={logoMastercard} alt="Mastercard" style={{ height: "25px" }} />
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                    <CardPayment
                        totalPrice={viewModel.getPaymentPrice}
                        propertyToProcessId={propertyId}
                        reservationRequestId={viewModel.getReservationStripePaymentRequestId}
                        orderRequestId={viewModel.getChoicesStripePaymentRequestId}
                        currentUserId={viewModel.currentUserId}
                        onCancel={() => {}} // No cancel needed as we don't have selection anymore
                    />
                    <Box mt={2} display="flex" alignItems="center" justifyContent="center">
                        <LockIcon style={{ fontSize: 16, marginRight: "8px", color: "#6b7c93" }} />
                        <Typography variant="caption" style={{ color: "#6b7c93" }}>
                            Your payment information is encrypted and secure
                        </Typography>
                    </Box>
                </Box>
            </Elements>
        );
    };

    const renderPaymentHistoryRow = (): JSX.Element => (
        <>
            <Box className={paymentHistoryStyles.paymentRow}>
                <Box>
                    <Typography className={paymentHistoryStyles.paymentRowTitle} variant="h5">
                        Payments history
                    </Typography>
                </Box>
                <Box style={{ display: "grid", gap: "20px" }}>
                    {viewModel.hasPaidStripeReservationPaymentRequest && (
                        <Box>
                            <Typography className={paymentHistoryStyles.paymentRowTitle} style={{ fontSize: "15px", marginBottom: "15px" }} variant="h6">
                                Reservation history
                            </Typography>
                            <Box className={paymentHistoryStyles.paymentRowSubTitle}>
                                <div>{viewModel.PaidReservationDate} - Reservation fee:</div>
                                <div>{viewModel.getReservationStripePaymentRequest}</div>
                            </Box>
                        </Box>
                    )}
                    {viewModel.hasPaidStripeOrderPaymentRequest && (
                        <Box>
                            <Typography className={paymentHistoryStyles.paymentRowTitle} style={{ fontSize: "15px", marginBottom: "15px" }} variant="h6">
                                Choices history
                            </Typography>
                            <Box className={paymentHistoryStyles.paymentRowSubTitle}>
                                <div>{viewModel.PaidOrderDate} - Choices fee:</div>
                                <div>{viewModel.getChoicesStripePaymentRequest}</div>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        </>
    );

    const renderPaymentSuccessMessage = (): JSX.Element => (
        <Box className={paymentSectionStyles.root} style={{ textAlign: "center", padding: "40px 20px" }}>
            <Paper elevation={3} style={{ padding: "30px", maxWidth: "600px", margin: "0 auto", borderRadius: "8px" }}>
                <CheckCircleOutlineIcon style={{ fontSize: 60, color: "#4caf50", marginBottom: "20px" }} />
                <Typography variant="h4" gutterBottom>
                    Payment Successful!
                </Typography>
                <Typography variant="body1" paragraph style={{ marginBottom: "20px" }}>
                    Thank you for your payment. Your transaction has been completed successfully.
                </Typography>
                <Typography variant="body2" paragraph style={{ color: "#666" }}>
                    A confirmation has been sent to your email address.
                </Typography>
                <Box mt={4}>
                    <Button variant="contained" color="primary" size="large" onClick={() => router.history.push("/")}>
                        Return to Dashboard
                    </Button>
                </Box>
            </Paper>
        </Box>
    );

    const renderPaymentSection = (): JSX.Element => {
        // If payment is successful, show the success message instead of the payment section
        if (paymentSuccess || buyViewModel.getValue("paymentSuccess")) {
            return renderPaymentSuccessMessage();
        }

        return (
            <Box className={paymentSectionStyles.root}>
                <Grid container spacing={0}>
                    <Grid item xs={12} sm={12}>
                        {renderPaymentHeader()}
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        {canDisplayPaymentRow() || canDisplayPaymentHistoryRow() ? (
                            <Box className={paymentSectionStyles.columnRow}>
                                <Box>
                                    <Box className={paymentSectionStyles.paymentHistoryCard}>
                                        {canDisplayPaymentRow() && renderPaymentDetailsRow()}
                                        {canDisplayPaymentHistoryRow() && <Box className="isDesktopPaymentHistory">{renderPaymentHistoryRow()}</Box>}
                                    </Box>
                                </Box>
                                <Box>{canDisplayPaymentRow() && <Box className={paymentSectionStyles.paymentMethodCard}>{renderPaymentMethod()}</Box>}</Box>

                                {!isDesktop && canDisplayPaymentHistoryRow() && <Box className="isMobilePaymentHistory">{renderPaymentHistoryRow()}</Box>}
                            </Box>
                        ) : (
                            <Typography variant="body2">No payment information available.</Typography>
                        )}
                    </Grid>
                </Grid>
            </Box>
        );
    };

    return useObserver(() => (
        <Box style={{ justifyContent: "unset" }} className={`${dashboardPageStyles.root} ${pageAnimationStyles.pageWrapper}`}>
            <Box style={{ width: "100%" }} className={dashboardPageStyles.workspace}>
                {viewModel.ApiStatus === ApiStatus.success && <React.Fragment>{renderPaymentSection()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.loading && <React.Fragment>{renderBusy()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.error && <React.Fragment>{renderError()}</React.Fragment>}
            </Box>
        </Box>
    ));
};
